import { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import Brand from "../components/Brand";

// Route A — /reset-password (no token): ask for email, send reset link
// Route B — /reset-password/:token   : ask for new password, submit
export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  async function requestLink(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/password-reset/request", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  async function submitNewPassword(e) {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/password-reset/confirm", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. You can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-[#FFFCF7] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-[#E8E2D6] rounded-lg shadow-sm p-8">
        <Brand variant="full" to="/login" size={36} />

        {!token && !sent && (
          <>
            <h1 className="font-display text-2xl font-bold text-[#0F2A14] mt-8">Reset your password.</h1>
            <p className="text-sm text-[#475569] mt-2">
              Enter your Purvodaya Solar email and we&apos;ll send you a secure link to set a new one.
            </p>
            <form onSubmit={requestLink} className="mt-6 space-y-4" data-testid="reset-request-form">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-[#475569] mb-1.5">Email</label>
                <input
                  type="email" required autoFocus
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="reset-email-input"
                  placeholder="you@company.com"
                  className="w-full bg-white border-2 border-[#E8E2D6] focus:border-[#F39C12] outline-none px-3 py-2.5 text-[#0F2A14]"
                />
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="reset-send-btn"
                className="w-full bg-[#1B5E20] text-white py-3 font-medium uppercase tracking-wider text-sm hover:bg-[#0F3D14] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send reset link"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
            <Link to="/login" data-testid="reset-back-link" className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#475569] hover:text-[#1B5E20]">
              <ArrowLeft className="w-3 h-3" /> Back to sign in
            </Link>
          </>
        )}

        {!token && sent && (
          <div className="mt-8 text-center" data-testid="reset-sent-panel">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1B5E20]" />
            <h1 className="font-display text-2xl font-bold text-[#0F2A14] mt-4">Check your inbox.</h1>
            <p className="text-sm text-[#475569] mt-2">
              If <span className="font-semibold text-[#0F2A14]">{email}</span> is registered with us, a password reset link is on the way. The link expires in 30 minutes.
            </p>
            <Link to="/login" className="mt-6 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-[#1B5E20] hover:underline">
              <ArrowLeft className="w-3 h-3" /> Return to sign in
            </Link>
          </div>
        )}

        {token && !done && (
          <>
            <h1 className="font-display text-2xl font-bold text-[#0F2A14] mt-8">Set a new password.</h1>
            <p className="text-sm text-[#475569] mt-2">Choose a strong password — at least 8 characters.</p>
            <form onSubmit={submitNewPassword} className="mt-6 space-y-4" data-testid="reset-confirm-form">
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-[#475569] mb-1.5">New password</label>
                <input
                  type="password" required autoFocus minLength={8}
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-new-password"
                  className="w-full bg-white border-2 border-[#E8E2D6] focus:border-[#F39C12] outline-none px-3 py-2.5 text-[#0F2A14]"
                />
              </div>
              <div>
                <label className="block text-xs font-medium uppercase tracking-wider text-[#475569] mb-1.5">Confirm new password</label>
                <input
                  type="password" required minLength={8}
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password"
                  className="w-full bg-white border-2 border-[#E8E2D6] focus:border-[#F39C12] outline-none px-3 py-2.5 text-[#0F2A14]"
                />
              </div>
              <button
                type="submit" disabled={loading}
                data-testid="reset-confirm-btn"
                className="w-full bg-[#1B5E20] text-white py-3 font-medium uppercase tracking-wider text-sm hover:bg-[#0F3D14] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {loading ? "Updating…" : "Update password"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </>
        )}

        {token && done && (
          <div className="mt-8 text-center" data-testid="reset-done-panel">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#1B5E20]" />
            <h1 className="font-display text-2xl font-bold text-[#0F2A14] mt-4">Password updated.</h1>
            <p className="text-sm text-[#475569] mt-2">Redirecting you to sign-in…</p>
          </div>
        )}
      </div>
    </div>
  );
}
